import React, { Component, Fragment } from 'react';
import Hammer from 'hammerjs';
import { TranslateContext } from 'core/translate';
import Frame from './Frame';
import Disclaimer from './Disclaimer';
import ConvertibleControls from './ConvertibleControls';
import CarouselButtons from './CarouselButtons';
import ControlButton from './ControlButton';
import ZoomButton from './ZoomButton';
import Pager from './Pager';

const INTERIOR_ANALYTICS_KEY = 'gallery:interior';
const EXTERIOR_ANALYTICS_KEY = 'gallery:exterior';

const ToggleInteriorButton = ({ isInterior, isVisible, onClick }) => {
  return (
    <TranslateContext.Consumer>
      {translate => (
        <ControlButton
          className={
            'image-controls__button image-controls__button--left stat-button-link'
          }
          text={translate(isInterior ? 'exterior' : 'interior')}
          screenReaderText={translate(
            isInterior
              ? 'component.cgi.toggle.exterior.label'
              : 'component.cgi.toggle.interior.label'
          )}
          icon={isInterior ? 'car' : 'tachometer'}
          isVisible={isVisible}
          analyticsKey={
            isInterior ? EXTERIOR_ANALYTICS_KEY : INTERIOR_ANALYTICS_KEY
          }
          onClick={onClick}
        />
      )}
    </TranslateContext.Consumer>
  );
};

const SingleImageControls = ({
  images,
  index,
  isInteriorPrimary,
  isInteriorToggleVisible,
  isZoomVisible,
  analyticsKey,
  onChangeIndex,
  onToggleInterior,
  onZoom
}) => {
  const className = [
    'image-controls__button',
    'image-controls__button--right',
    'stat-button-link',
    isZoomVisible ? '' : 'invisible'
  ].join(' ');

  return (
    <div className="image-controls">
      <ToggleInteriorButton
        isVisible={isInteriorToggleVisible}
        isInterior={isInteriorPrimary}
        onClick={onToggleInterior}
      />
      <Pager
        images={images}
        index={index}
        analyticsKey={analyticsKey}
        onChangeIndex={onChangeIndex}
        baseClassName={'image-controls'}
      />
      <ZoomButton
        onClick={() => onZoom(index)}
        text={'enlarge'}
        className={className}
        isVisible={isZoomVisible}
      />
    </div>
  );
};

class SingleImageCarousel extends Component {
  constructor(props) {
    super(props);

    this.ref = React.createRef();

    this.onPrev = this.onPrev.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onChangeIndex = this.onChangeIndex.bind(this);
  }

  componentDidMount() {
    this.hammer = Hammer(this.ref.current);
    this.hammer.on('swipeleft', this.onNext);
    this.hammer.on('swiperight', this.onPrev);
  }

  componentWillUnmount() {
    this.hammer.off('swipeleft', this.onNext);
    this.hammer.off('swiperight', this.onPrev);
  }

  onPrev() {
    const images = this.getImages();

    if (images.length > 0) {
      const index = this.getIndex();
      const prevIndex = index - 1 >= 0 ? index - 1 : images.length - 1;
      this.onChangeIndex(prevIndex);
    }
  }

  onNext() {
    const images = this.getImages();

    if (images.length > 0) {
      const index = this.getIndex();
      const nextIndex = index + 1 < images.length ? index + 1 : 0;
      this.onChangeIndex(nextIndex);
    }
  }

  onChangeIndex(index) {
    this.props.isInteriorPrimary
      ? this.props.onChangeInteriorIndex(index)
      : this.props.onChangeExteriorIndex(index);
  }

  getImages() {
    const { exteriorImages, interiorImages, isInteriorPrimary } = this.props;
    return isInteriorPrimary ? interiorImages : exteriorImages;
  }

  getIndex() {
    const { interiorIndex, exteriorIndex, isInteriorPrimary } = this.props;
    return isInteriorPrimary ? interiorIndex : exteriorIndex;
  }

  render() {
    const {
      cgiDisclaimers,
      isInteriorPrimary,
      isConvertible,
      isTopDown,
      isInteriorToggleVisible,
      onToggleTop,
      onToggleInterior,
      onZoomExterior,
      onZoomInterior,
      style
    } = this.props;

    const analyticsKey = isInteriorPrimary
      ? INTERIOR_ANALYTICS_KEY
      : EXTERIOR_ANALYTICS_KEY;

    const images = this.getImages();
    const index = this.getIndex();
    const image = images[index];

    return (
      <Fragment>
        <div className="carousel carousel--single" ref={this.ref} style={style}>
          <Disclaimer disclaimers={cgiDisclaimers} />
          <CarouselButtons
            isVisible={images.length > 1}
            analyticsKey={analyticsKey}
            onPrev={this.onPrev}
            onNext={this.onNext}
          />
          <ConvertibleControls
            enabled={isConvertible}
            isTopDown={isTopDown}
            analyticsKey={analyticsKey}
            onToggle={onToggleTop}
          />
          <Frame url={image.url} />
        </div>
        <SingleImageControls
          images={images}
          index={index}
          isInteriorPrimary={isInteriorPrimary}
          isInteriorToggleVisible={isInteriorToggleVisible}
          isZoomVisible={!!image.zoom}
          analyticsKey={analyticsKey}
          onChangeIndex={this.onChangeIndex}
          onToggleInterior={onToggleInterior}
          onZoom={isInteriorPrimary ? onZoomInterior : onZoomExterior}
        />
      </Fragment>
    );
  }
}

export default SingleImageCarousel;
